import { MutationCache, QueryClient } from "@tanstack/react-query";
import { toast } from "./use-toast";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.ok) {
    const type = res.headers.get("content-type") ?? "";
    return (type.includes("application/json") ? res.json() : (undefined as unknown)) as Promise<T>;
  }
  let body: { error?: { code?: string; message?: string; details?: unknown } } = {};
  try {
    body = await res.json();
  } catch {
    /* not JSON */
  }
  const fallback =
    res.status === 429
      ? "Too many attempts. Please wait 15 minutes and try again."
      : res.status >= 500
        ? "ClientFlow encountered a server error. Please try again in a moment."
        : "The request could not be completed. Please try again.";
  throw new ApiError(res.status, body.error?.code ?? (res.status === 429 ? "rate_limited" : "error"), body.error?.message ?? fallback, body.error?.details);
}

const CSRF = { "x-clientflow-csrf": "1" };

export const api = {
  get: <T,>(url: string) => fetch(url, { credentials: "same-origin" }).then((r) => handle<T>(r)),
  /** `idempotencyKey`: the same for every send of one submission, so QuickBooks creates it once. */
  send: <T,>(method: "POST" | "PUT" | "PATCH" | "DELETE", url: string, body?: unknown, idempotencyKey?: string) =>
    fetch(url, {
      method,
      credentials: "same-origin",
      headers: { ...CSRF, ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }).then((r) => handle<T>(r)),
  upload: <T,>(url: string, fields: Record<string, string | undefined>, file: File) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== "") form.append(k, v);
    form.append("file", file);
    return fetch(url, { method: "POST", credentials: "same-origin", headers: CSRF, body: form }).then((r) => handle<T>(r));
  },
  /** Several files in one request, under the field "files". */
  uploadFiles: <T,>(url: string, fields: Record<string, string | undefined>, files: File[]) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== "") form.append(k, v);
    for (const f of files) form.append("files", f);
    return fetch(url, { method: "POST", credentials: "same-origin", headers: CSRF, body: form }).then((r) => handle<T>(r));
  },
};

export const queryClient: QueryClient = new QueryClient({
  // After any change, refresh everything on screen (except the session), so
  // lists, counts and badges never show stale data. Cheap at firm scale.
  mutationCache: new MutationCache({
    onSuccess: () => queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "/api/auth/me" }),
    onError: (err, _vars, _ctx, mutation) => {
      if (!mutation.options.onError && !mutation.options.meta?.inlineError) toast({ title: "Action not completed", description: errorMessage(err), variant: "destructive" });
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: ({ queryKey }) => api.get(queryKey.join("")),
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  },
});

/** Turns API validation details into a readable sentence. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const fieldErrors = (err.details as { fieldErrors?: Record<string, string[]> } | undefined)?.fieldErrors;
    const first = fieldErrors && Object.entries(fieldErrors).find(([, v]) => v?.length);
    return first ? `${first[0]}: ${first[1][0]}` : err.message;
  }
  return "The request could not be completed. Please try again.";
}
